import { ApiTags } from '@constants/common';
import { GenericApiResponse } from '@type/general';
import restApi from '@services/api';

import {
  AIF,
  BlockIcfRequest,
  CreateDealRequest,
  CreateDealResponse,
  DealNameListType,
  Deals,
  DeleteCompanyFounderRequestType,
  DeleteDealContentRequestType,
  DeleteDealDocumentRequestType,
  DeleteDealHighlightRequestType,
  DeleteDealMetricRequestType,
  DeleteDealRequestType,
  DeleteInstitutionalInvestorRequestType,
  DeleteInvestorRequestType,
  DeleteNewsRequestType,
  DeleteQuickLinkRequestType,
  DeleteSuperAngelRequestType,
  GeneratePdfRequestType,
  GeneratePdfRespnseType,
  GetDealListResponse,
  GetDealResponse,
  GetMoneyBallDealListResponse,
  GetMoneyBallListResponse,
  QuickLinkEntry,
  UpdateDealRequest,
  UpdateDealResponse,
  ValidateNewsRequest,
  ValidateNewsResponse
} from './types';

const GetDealApi = restApi.injectEndpoints({
  endpoints: builder => ({
    getDeal: builder.query<Deals, { dealId?: string }>({
      query: payload => ({
        url: `${process.env.REACT_APP_BASE_API_VERSION}/support/deal/${payload.dealId}`,
        method: 'GET'
      }),
      providesTags: [ApiTags.deal, ApiTags.recordInvestment],
      transformResponse: (response: GetDealResponse) => response.result
    }),
    getDealList: builder.query<DealNameListType[], { companyId?: string }>({
      query: payload => ({
        url: `${
          process.env.REACT_APP_BASE_API_VERSION
        }/support/deal/list/name?${
          payload.companyId ? `&company_id=${payload.companyId}` : ''
        }`,
        method: 'GET'
      }),
      providesTags: [ApiTags.dealList],
      transformResponse: (response: GetDealListResponse): DealNameListType[] =>
        response.result.deal_name_list ?? []
    }),
    createDeal: builder.mutation<CreateDealResponse, CreateDealRequest>({
      query: payload => ({
        url: `${process.env.REACT_APP_BASE_API_VERSION}/support/deal`,
        method: 'POST',
        body: payload
      }),
      invalidatesTags: [ApiTags.dealList, ApiTags.deal],
      transformResponse: (response: CreateDealResponse) => response
    }),
    updateDeal: builder.mutation<UpdateDealResponse, UpdateDealRequest>({
      query: payload => ({
        url: `${process.env.REACT_APP_BASE_API_VERSION}/support/deal/${payload.id}`,
        method: 'PUT',
        body: payload
      }),
      invalidatesTags: [ApiTags.dealList, ApiTags.deal],
      transformResponse: (response: CreateDealResponse) => response
    }),
    generateIcf: builder.mutation<
      GeneratePdfRespnseType,
      GeneratePdfRequestType
    >({
      query: payload => ({
        url: `${process.env.REACT_APP_BASE_API_VERSION}/support/utils/generate-doc/icf`,
        method: 'POST',
        body: payload
      }),
      transformResponse: (response: GeneratePdfRespnseType) => response
    }),
    generateDrawdown: builder.mutation<
      GeneratePdfRespnseType,
      GeneratePdfRequestType
    >({
      query: payload => ({
        url: `${process.env.REACT_APP_BASE_API_VERSION}/support/utils/generate-doc/drawdown`,
        method: 'POST',
        body: payload
      }),
      transformResponse: (response: GeneratePdfRespnseType) => response
    }),
    deleteDealHighlight: builder.mutation<
      GenericApiResponse<string>,
      DeleteDealHighlightRequestType
    >({
      query: payload => ({
        // eslint-disable-next-line max-len
        url: `${process.env.REACT_APP_BASE_API_VERSION}/support/deal/${payload.deal_id}/deal-highlight/${payload.deal_highlight_id}`,
        method: 'DELETE'
      }),
      transformResponse: (response: GenericApiResponse<string>) => response
    }),
    deleteDeal: builder.mutation<
      GenericApiResponse<string>,
      DeleteDealRequestType
    >({
      query: payload => ({
        url: `${process.env.REACT_APP_BASE_API_VERSION}/support/deal/${payload.deal_id}`,
        method: 'DELETE'
      }),
      invalidatesTags: [
        ApiTags.dealList,
        ApiTags.deal,
        ApiTags.earlyAccess,
        ApiTags.wishlist
      ],
      transformResponse: (response: GenericApiResponse<string>) => response
    }),
    deleteDealDocument: builder.mutation<
      GenericApiResponse<string>,
      DeleteDealDocumentRequestType
    >({
      query: payload => ({
        // eslint-disable-next-line max-len
        url: `${process.env.REACT_APP_BASE_API_VERSION}/support/deal/${payload.deal_id}/deal-document/${payload.deal_document_id}`,
        method: 'DELETE'
      }),
      invalidatesTags: [ApiTags.deal],
      transformResponse: (response: GenericApiResponse<string>) => response
    }),
    deleteCompanyFounder: builder.mutation<
      GenericApiResponse<string>,
      DeleteCompanyFounderRequestType
    >({
      query: payload => ({
        // eslint-disable-next-line max-len
        url: `${process.env.REACT_APP_BASE_API_VERSION}/support/company/${payload.company_id}/company-founder/${payload.founder_id}`,
        method: 'DELETE'
      }),
      invalidatesTags: [ApiTags.deal],
      transformResponse: (response: GenericApiResponse<string>) => response
    }),
    deleteInstitutionalInvestor: builder.mutation<
      GenericApiResponse<string>,
      DeleteInstitutionalInvestorRequestType
    >({
      query: payload => ({
        // eslint-disable-next-line max-len
        url: `${process.env.REACT_APP_BASE_API_VERSION}/support/company/${payload.company_id}/company-institutional-investor/${payload.institutional_investor_id}`,
        method: 'DELETE'
      }),
      invalidatesTags: [ApiTags.deal],
      transformResponse: (response: GenericApiResponse<string>) => response
    }),
    deleteInvestor: builder.mutation<
      GenericApiResponse<string>,
      DeleteInvestorRequestType
    >({
      query: payload => ({
        // eslint-disable-next-line max-len
        url: `${process.env.REACT_APP_BASE_API_VERSION}/support/deal/${payload.deal_id}/deal-investor/${payload.investor_id}`,
        method: 'DELETE'
      }),
      invalidatesTags: [ApiTags.deal],
      transformResponse: (response: GenericApiResponse<string>) => response
    }),
    deleteSuperAngel: builder.mutation<
      GenericApiResponse<string>,
      DeleteSuperAngelRequestType
    >({
      query: payload => ({
        // eslint-disable-next-line max-len
        url: `${process.env.REACT_APP_BASE_API_VERSION}/support/company/${payload.company_id}/company-super-angle/${payload.super_angel_id}`,
        method: 'DELETE'
      }),
      invalidatesTags: [ApiTags.deal],
      transformResponse: (response: GenericApiResponse<string>) => response
    }),
    getQuickLinkEntries: builder.query<QuickLinkEntry, void>({
      query: () => ({
        url: `${process.env.REACT_APP_BASE_API_VERSION}/support/quick-link`,
        method: 'GET'
      }),
      transformResponse: (response: GenericApiResponse<QuickLinkEntry>) =>
        response.result
    }),
    deleteDealContent: builder.mutation<
      GenericApiResponse<string>,
      DeleteDealContentRequestType
    >({
      query: payload => ({
        // eslint-disable-next-line max-len
        url: `${process.env.REACT_APP_BASE_API_VERSION}/support/deal/${payload.deal_id}/deal-content/${payload.content_id}`,
        method: 'DELETE'
      }),
      invalidatesTags: [ApiTags.deal],
      transformResponse: (response: GenericApiResponse<string>) => response
    }),
    deleteDealMetrics: builder.mutation<
      GenericApiResponse<string>,
      DeleteDealMetricRequestType
    >({
      query: payload => ({
        // eslint-disable-next-line max-len
        url: `${process.env.REACT_APP_BASE_API_VERSION}/support/deal/${payload.deal_id}/deal-highlight-metrics/${payload.metric_id}`,
        method: 'DELETE'
      }),
      invalidatesTags: [ApiTags.deal],
      transformResponse: (response: GenericApiResponse<string>) => response
    }),
    deleteQuickLink: builder.mutation<
      GenericApiResponse<string>,
      DeleteQuickLinkRequestType
    >({
      query: payload => ({
        // eslint-disable-next-line max-len
        url: `${process.env.REACT_APP_BASE_API_VERSION}/support/quick-link/${payload.quick_link_id}`,
        method: 'DELETE'
      }),
      invalidatesTags: [ApiTags.deal],
      transformResponse: (response: GenericApiResponse<string>) => response
    }),
    deleteNews: builder.mutation<
      GenericApiResponse<string>,
      DeleteNewsRequestType
    >({
      query: payload => ({
        // eslint-disable-next-line max-len
        url: `${process.env.REACT_APP_BASE_API_VERSION}/support/news/${payload.news_id}`,
        method: 'DELETE'
      }),
      invalidatesTags: [ApiTags.deal],
      transformResponse: (response: GenericApiResponse<string>) => response
    }),
    getAIF: builder.query<AIF[], void>({
      query: () => ({
        url: `${process.env.REACT_APP_BASE_API_VERSION}/support/aif`,
        method: 'GET'
      }),
      providesTags: [],
      transformResponse: (response: GenericApiResponse<AIF[]>) =>
        response.result
    }),
    validateNews: builder.mutation<ValidateNewsResponse, ValidateNewsRequest>({
      query: payload => ({
        url: `${process.env.REACT_APP_BASE_API_VERSION}/support/utils/check-link-scrap`,
        method: 'POST',
        body: payload
      }),
      invalidatesTags: [ApiTags.deal]
    }),
    blockIcf: builder.mutation<GenericApiResponse<string>, BlockIcfRequest>({
      query: payload => ({
        url: `${process.env.REACT_APP_BASE_API_VERSION}/support/deal/${payload.dealId}/block-icf-generation`,
        method: 'POST',
        body: {
          block_icf: payload.blockICF,
          send_notification: payload.sendNotification
        }
      }),
      invalidatesTags: [ApiTags.deal]
    }),

    // get moneyball list
    getMoneyBall: builder.query<GetMoneyBallListResponse, void>({
      query: () => ({
        url: `${process.env.REACT_APP_BASE_API_VERSION}/support/moneyball/`,
        method: 'GET'
      })
    }),

    // get moneyball deals list
    getMoneyBallDeals: builder.query<
      GetMoneyBallDealListResponse,
      { moneyballId: string }
    >({
      query: ({ moneyballId }) => ({
        url: `${process.env.REACT_APP_BASE_API_VERSION}/support/moneyball/${moneyballId}`,
        method: 'GET'
      })
    })
  })
});

export const {
  useLazyGetDealQuery,
  useLazyGetDealListQuery,
  useGetDealListQuery,
  useCreateDealMutation,
  useGenerateIcfMutation,
  useGenerateDrawdownMutation,
  useDeleteDealHighlightMutation,
  useDeleteDealMutation,
  useDeleteDealDocumentMutation,
  useDeleteCompanyFounderMutation,
  useDeleteInstitutionalInvestorMutation,
  useUpdateDealMutation,
  useDeleteInvestorMutation,
  useDeleteSuperAngelMutation,
  useGetQuickLinkEntriesQuery,
  useDeleteDealContentMutation,
  useDeleteDealMetricsMutation,
  useDeleteNewsMutation,
  useDeleteQuickLinkMutation,
  useGetAIFQuery,
  useValidateNewsMutation,
  useBlockIcfMutation,
  useGetMoneyBallQuery,
  useGetMoneyBallDealsQuery
} = GetDealApi;
